import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Clock, Copy, Frown, Meh, Smile, Trash2, Check, Edit, Image, Keyboard, Plus, Minus, Calendar } from 'lucide-react';
import { TodoScreenshot } from './TodoScreenshot';
import { TimeboxedDay } from './TimeboxedDay';
import Cookies from 'js-cookie';
import type { Todo } from './types';

const difficultyEmojis = [
  { icon: Smile, label: 'Easy', color: 'text-green-400' },
  { icon: Meh, label: 'Medium', color: 'text-orange-400' },
  { icon: Frown, label: 'Hard', color: 'text-red-400' }
];

const difficultyTextEmojis = ['😊', '😐', '😣'];

const wisdomQuotes = [
  "Be yourself—everyone else is already taken and slightly disappointing.",
  "You miss 100% of the naps you don't take.",
  "Dream big. Then go take a snack break.",
  "If at first you don't succeed, redefine success.",
  "Believe in yourself, especially when no one else does. Like Mondays.",
  "Failure is not the opposite of success—it's the hilarious prequel.",
  "Keep going. Nobody knows what they're doing either.",
  "Do what you love, even if you're bad at it. Especially then.",
  "Stay positive. Test negative. That's just science.",
  "You can do anything. Just not today.",
  "The early bird gets the worm... but the second mouse gets the cheese.",
  "Progress, not perfection. Unless you're cutting your own hair.",
  "You are enough. But coffee helps.",
  "One day or day one. Either way, I'm still in bed.",
  "Don't quit your daydream—unless it's about emails.",
  "Think outside the box. Then nap inside it.",
  "You have what it takes... to procrastinate like a pro.",
  "Work smarter, not harder. Or just act really busy.",
  "You're a limited edition. Probably for a reason.",
  "Chase your dreams. If they run, they weren't meant for you."
];

const STORAGE_KEY = 'paperList';
const NAME_KEY = 'paperListName';
const SCREENSHOT_ENABLED_KEY = 'screenshotEnabled';
const TIMELINE_ENABLED_KEY = 'timelineEnabled';

function App() {
  const [todos, setTodos] = useState<Todo[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : [];
  });
  const [newTodo, setNewTodo] = useState('');
  const [copied, setCopied] = useState(false);
  const [name, setName] = useState(() => {
    return localStorage.getItem(NAME_KEY) || 'Your';
  });
  const [editingName, setEditingName] = useState(false);
  const [editingTodoId, setEditingTodoId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState('');
  const [screenshotEnabled, setScreenshotEnabled] = useState(() => {
    const saved = Cookies.get(SCREENSHOT_ENABLED_KEY);
    return saved === 'true';
  });
  const [timelineEnabled, setTimelineEnabled] = useState(() => {
    const saved = Cookies.get(TIMELINE_ENABLED_KEY);
    return saved === 'true';
  });
  const [selectedTodoIndex, setSelectedTodoIndex] = useState<number>(-1);
  const [showShortcuts, setShowShortcuts] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const taskText = params.get('create');
    const timeEstimate = params.get('estimate');

    if (taskText && timeEstimate) {
      const newTodo: Todo = {
        id: Date.now() + Math.random().toString(),
        text: decodeURIComponent(taskText),
        note: '',
        difficulty: 0,
        timeEstimate: timeEstimate,
        completed: false
      };

      setTodos(prevTodos => [newTodo, ...prevTodos]);
      
      // Clear URL parameters
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(todos));
  }, [todos]);

  useEffect(() => {
    localStorage.setItem(NAME_KEY, name);
  }, [name]);

  useEffect(() => {
    Cookies.set(SCREENSHOT_ENABLED_KEY, String(screenshotEnabled), { expires: 365 });
  }, [screenshotEnabled]);

  useEffect(() => {
    Cookies.set(TIMELINE_ENABLED_KEY, String(timelineEnabled), { expires: 365 });
  }, [timelineEnabled]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isEditing = editingTodoId !== null || editingName || document.activeElement?.tagName === 'TEXTAREA' || document.activeElement?.tagName === 'INPUT';

      if (!isEditing) {
        if (e.key === 'Enter' && newTodo.trim()) {
          e.preventDefault();
          handleAddTodo(e as any);
        } else if (e.key === '.' && !newTodo.trim()) {
          e.preventDefault();
          const textarea = document.querySelector('textarea');
          if (textarea) {
            textarea.focus();
          }
        }
      }

      if (todos.length > 0 && !isEditing && ((e.altKey && e.key === 'c') || (e.metaKey && e.key === 'c' && !e.shiftKey && !e.altKey))) {
        e.preventDefault();
        copyToClipboard();
      }

      if (!isEditing) {
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setSelectedTodoIndex(prev => Math.max(0, prev === -1 ? todos.length - 1 : prev - 1));
        } else if (e.key === 'ArrowDown') {
          e.preventDefault();
          setSelectedTodoIndex(prev => Math.min(todos.length - 1, prev + 1));
        } else if (e.key === 'ArrowLeft' && selectedTodoIndex !== -1) {
          e.preventDefault();
          adjustTimeEstimate(todos[selectedTodoIndex].id, false);
        } else if (e.key === 'ArrowRight' && selectedTodoIndex !== -1) {
          e.preventDefault();
          adjustTimeEstimate(todos[selectedTodoIndex].id, true);
        }
      }

      if (e.key === 'x' && selectedTodoIndex !== -1 && !isEditing) {
        e.preventDefault();
        toggleComplete(todos[selectedTodoIndex].id);
      }

      if (e.key === 'e' && selectedTodoIndex !== -1 && !isEditing) {
        e.preventDefault();
        startEditing(todos[selectedTodoIndex]);
      }

      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedTodoIndex !== -1 && !isEditing) {
        e.preventDefault();
        deleteTodo(todos[selectedTodoIndex].id);
        setSelectedTodoIndex(prev => Math.min(prev, todos.length - 2));
      }

      if (['1', '2', '3'].includes(e.key) && selectedTodoIndex !== -1 && !isEditing) {
        e.preventDefault();
        const newDifficulty = parseInt(e.key) - 1;
        setTodos(todos.map((todo, index) => 
          index === selectedTodoIndex ? { ...todo, difficulty: newDifficulty } : todo
        ));
      }

      if (e.key === '?' && !isEditing) {
        e.preventDefault();
        setShowShortcuts(prev => !prev);
      }

      if (e.key === 't' && !isEditing) {
        e.preventDefault();
        setTimelineEnabled(prev => !prev);
      }

      if (e.key === 's' && !isEditing) {
        e.preventDefault();
        setTimelineEnabled(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [todos, selectedTodoIndex, newTodo, editingTodoId, editingName]);

  const handleAddTodo = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTodo.trim()) return;
    
    const tasks = newTodo.split('\n').filter(task => task.trim());
    const newTodos = tasks.map(task => {
      const [title, note] = task.split(': ').map(s => s.trim());
      return {
        id: Date.now() + Math.random().toString(),
        text: title,
        note: note || '',
        difficulty: 0,
        timeEstimate: '1h',
        completed: false
      };
    });
    
    setTodos([...todos, ...newTodos]);
    setNewTodo('');
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;
    
    const items = Array.from(todos);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    setTodos(items);
    setSelectedTodoIndex(result.destination.index);
  };

  const startEditing = (todo: Todo) => {
    setEditingTodoId(todo.id);
    setEditingText(todo.text + (todo.note ? `: ${todo.note}` : ''));
  };

  const saveEdit = () => {
    if (!editingTodoId) return;
    
    const [title, note] = editingText.split(': ').map(s => s.trim());
    setTodos(todos.map(todo =>
      todo.id === editingTodoId
        ? { ...todo, text: title, note: note || '' }
        : todo
    ));
    setEditingTodoId(null);
    setEditingText('');
  };

  const handleEditKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      saveEdit();
    } else if (e.key === 'Escape') {
      setEditingTodoId(null);
      setEditingText('');
    }
  };

  const updateDifficulty = (id: string) => {
    setTodos(todos.map(todo => 
      todo.id === id 
        ? { ...todo, difficulty: (todo.difficulty + 1) % 3 }
        : todo
    ));
  };

  const updateTimeEstimate = (id: string, value: string) => {
    setTodos(todos.map(todo =>
      todo.id === id
        ? { ...todo, timeEstimate: value }
        : todo
    ));
  };

  const adjustTimeEstimate = (id: string, increment: boolean) => {
    setTodos(todos.map(todo => {
      if (todo.id !== id) return todo;
      
      const current = todo.timeEstimate;
      let minutes = 0;
      
      if (current.includes('h')) {
        const [h, m] = current.split('h');
        minutes = parseInt(h) * 60 + (m ? parseInt(m) : 0);
      } else {
        minutes = parseInt(current);
      }
      
      minutes += increment ? 15 : -15;
      minutes = Math.max(15, minutes);
      
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      
      return {
        ...todo,
        timeEstimate: hours > 0 
          ? `${hours}h${remainingMinutes > 0 ? remainingMinutes : ''}`
          : `${minutes}m`
      };
    }));
  };

  const toggleComplete = (id: string) => {
    setTodos(todos.map(todo =>
      todo.id === id
        ? { ...todo, completed: !todo.completed }
        : todo
    ));
  };

  const deleteTodo = (id: string) => {
    setTodos(todos.filter(todo => todo.id !== id));
  };

  const copyToClipboard = () => {
    if (todos.length === 0) return;
    
    const maxLength = Math.max(10, ...todos.map(todo => todo.text.length));
    
    const formattedText = todos.map(todo => {
      const status = todo.completed ? '[x]' : '[ ]';
      const text = todo.text.padEnd(maxLength);
      const difficulty = difficultyTextEmojis[todo.difficulty];
      const time = ` ${todo.timeEstimate}`;
      
      let lines = [`${status} ${text} ${difficulty}${time}`];
      if (todo.note) {
        const indent = ' '.repeat(status.length + 1);
        const subTasks = todo.note.split(',').map(task => task.trim());
        subTasks.forEach(task => {
          lines.push(`${indent}└─ ${task}`);
        });
      }
      return lines.join('\n');
    }).join('\n');
    
    const separatorLength = Math.max(15, maxLength + 15);
    const separator = '─'.repeat(separatorLength);
    
    const header = 
      `📋 ${name}'s paper list\n` +
      `${separator}\n` +
      `Status Task${' '.repeat(Math.max(0, maxLength - 4))}Lvl Time\n` +
      `${separator}\n`;
    
    const randomQuote = wisdomQuotes[Math.floor(Math.random() * wisdomQuotes.length)];
    const footer = `\n${separator}\n${name === 'Shaun' ? '  (⎺‿⎺)凸\n' : ''}${randomQuote}`;
    
    navigator.clipboard.writeText(header + formattedText + footer);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] pb-[160px]">
      <div className="p-4">
        <div className="max-w-2xl mx-auto w-full space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {editingName ? (
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onBlur={() => setEditingName(false)}
                  onKeyDown={(e) => e.key === 'Enter' && setEditingName(false)}
                  className="text-2xl bg-transparent text-[#a0a0a0] border-b border-[#404040] focus:outline-none terminal-input"
                  autoFocus
                />
              ) : (
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl text-[#a0a0a0]">
                    <span 
                      className="cursor-pointer hover:text-[#c0c0c0] border-b border-dashed border-[#404040]"
                      onClick={() => setEditingName(true)}
                    >
                      {name}
                    </span>'s paper list
                  </h1>
                  <button 
                    onClick={() => setEditingName(true)}
                    className="text-[#606060] hover:text-[#c0c0c0]"
                    title="Edit name"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setTimelineEnabled(!timelineEnabled)}
                className={`flex items-center gap-2 px-3 py-1 rounded border ${
                  timelineEnabled
                    ? 'text-[#c0c0c0] border-[#606060]'
                    : 'text-[#606060] border-[#404040]'
                } hover:text-[#c0c0c0] hover:border-[#606060] transition-colors`}
                title={timelineEnabled ? 'Hide timeline (s)' : 'Show timeline (s)'}
              >
                <Calendar className="w-4 h-4" />
              </button>
              <button
                onClick={() => setShowShortcuts(!showShortcuts)}
                className={`flex items-center gap-2 px-3 py-1 rounded border ${
                  showShortcuts
                    ? 'text-[#c0c0c0] border-[#606060]'
                    : 'text-[#606060] border-[#404040]'
                } hover:text-[#c0c0c0] hover:border-[#606060] transition-colors`}
                title="Toggle keyboard shortcuts"
              >
                <Keyboard className="w-4 h-4" />
              </button>
              <button
                onClick={() => setScreenshotEnabled(!screenshotEnabled)}
                className={`flex items-center gap-2 px-3 py-1 rounded border ${
                  screenshotEnabled
                    ? 'text-[#c0c0c0] border-[#606060]'
                    : 'text-[#606060] border-[#404040]'
                } hover:text-[#c0c0c0] hover:border-[#606060] transition-colors`}
                title={screenshotEnabled ? 'Disable screenshots' : 'Enable screenshots'}
              >
                <Image className="w-4 h-4" />
              </button>
            </div>
          </div>

          {showShortcuts && (
            <div className="text-[#606060] text-sm border border-[#404040] p-4 rounded bg-[#0a0a0a]">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <span className="text-[#a0a0a0]">.</span> Focus input
                  <br />
                  <span className="text-[#a0a0a0]">Enter</span> Add task
                  <br />
                  <span className="text-[#a0a0a0]">↑/↓</span> Navigate tasks
                  <br />
                  <span className="text-[#a0a0a0]">←/→</span> Adjust time
                </div>
                <div>
                  <span className="text-[#a0a0a0]">x</span> Toggle complete
                  <br />
                  <span className="text-[#a0a0a0]">e</span> Edit task
                  <br />
                  <span className="text-[#a0a0a0]">1/2/3</span> Set difficulty
                  <br />
                  <span className="text-[#a0a0a0]">s</span> Toggle timeline
                </div>
              </div>
            </div>
          )}
          
          <form onSubmit={handleAddTodo}>
            <div className="space-y-2">
              <textarea
                value={newTodo}
                onChange={(e) => setNewTodo(e.target.value)}
                placeholder="Add tasks (one per line)..."
                rows={3}
                className="w-full border-[#404040] bg-[#0a0a0a] text-[#a0a0a0] px-2 py-1 terminal-input"
              />
              <button
                type="submit"
                className="bg-[#0a0a0a] text-[#a0a0a0] border border-[#404040] px-4 py-1 hover:border-[#606060] hover:text-[#c0c0c0]"
              >
                Add Tasks
              </button>
            </div>
          </form>

          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="todos">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="space-y-1"
                >
                  {todos.map((todo, index) => (
                    <Draggable key={todo.id} draggableId={todo.id} index={index}>
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className={`group bg-[#0a0a0a] p-2 border ${
                            index === selectedTodoIndex
                              ? 'border-[#606060]'
                              : 'border-[#404040]'
                          } flex items-center gap-3`}
                        >
                          <button
                            onClick={() => toggleComplete(todo.id)}
                            className={`p-1 ${todo.completed ? 'text-[#606060]' : 'text-[#a0a0a0] hover:text-[#c0c0c0]'}`}
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          {editingTodoId === todo.id ? (
                            <input
                              type="text"
                              value={editingText}
                              onChange={(e) => setEditingText(e.target.value)}
                              onBlur={saveEdit}
                              onKeyDown={handleEditKeyDown}
                              className="flex-1 bg-[#0a0a0a] text-[#a0a0a0] border-b border-[#404040] focus:outline-none terminal-input"
                              autoFocus
                            />
                          ) : (
                            <div className="flex-1 flex items-center gap-2">
                              <span 
                                className={`flex-1 cursor-text ${todo.completed ? 'line-through text-[#606060]' : 'text-[#a0a0a0]'}`}
                                onClick={() => !todo.completed && startEditing(todo)}
                              >
                                {todo.text}{todo.note ? `: ${todo.note}` : ''}
                              </span>
                              <button
                                onClick={() => !todo.completed && startEditing(todo)}
                                className="opacity-0 group-hover:opacity-100 p-1 text-[#a0a0a0] hover:text-[#c0c0c0]"
                                title="Edit task"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                          <button
                            onClick={() => updateDifficulty(todo.id)}
                            className={`p-1 ${difficultyEmojis[todo.difficulty].color}`}
                            title={difficultyEmojis[todo.difficulty].label}
                          >
                            {React.createElement(difficultyEmojis[todo.difficulty].icon, {
                              className: "w-4 h-4"
                            })}
                          </button>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => adjustTimeEstimate(todo.id, false)}
                              className="p-1 text-[#a0a0a0] hover:text-[#c0c0c0]"
                              title="Decrease time (-15m)"
                            >
                              <Minus className="w-4 h-4" />
                            </button>
                            <input
                              type="text"
                              value={todo.timeEstimate}
                              onChange={(e) => updateTimeEstimate(todo.id, e.target.value)}
                              className="w-12 text-center bg-[#0a0a0a] border border-[#404040] text-[#a0a0a0] terminal-input"
                            />
                            <button
                              onClick={() => adjustTimeEstimate(todo.id, true)}
                              className="p-1 text-[#a0a0a0] hover:text-[#c0c0c0]"
                              title="Increase time (+15m)"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                          <button
                            onClick={() => deleteTodo(todo.id)}
                            className="p-1 text-[#a0a0a0] hover:text-[#ff4040]"
                            title="Delete task"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          {todos.length > 0 && (
            <div className="space-y-2">
              <button
                onClick={copyToClipboard}
                className="flex items-center gap-2 bg-[#0a0a0a] text-[#a0a0a0] border border-[#404040] px-4 py-1 hover:border-[#606060] hover:text-[#c0c0c0]"
              >
                <Copy className="w-4 h-4" />
                {copied ? 'Copied!' : 'Copy formatted list'}
              </button>
              
              {screenshotEnabled && <TodoScreenshot todos={todos} name={name} />}
            </div>
          )}
        </div>
      </div>

      {timelineEnabled && (
        <div className="fixed bottom-0 left-0 right-0 z-50">
          <TimeboxedDay todos={todos} />
        </div>
      )}
    </div>
  );
}

export default App;